"use client"
import { useEffect, useState } from "react"
import { Component } from "@/types/types"
import { getServiceStatus } from "@/actions/_archive/global/service/getServiceStatus"
import { ServiceStatusView } from "@/ui/views/global/ServiceStatusView"
import { useNotifications } from "@/hooks/_archive/global/notifications/useNotifications"
import { dateStringToDate } from "@/utils/date/dateStringToDate"
import { format, isPast } from "date-fns"
import { stringToHash } from "@/utils/stringToHash"

export const presets = {}

export interface Props extends Component {
  assetsBucket: string
}

export function ServiceStatusProvider({ children, className = "", assetsBucket }: Props) {
  const [status, setStatus] = useState<"loading" | "down" | "up">("loading")
  const [_, addNotifications, dismissNotification] = useNotifications()

  async function setup() {
    const [serviceStatus] = await getServiceStatus()
    setStatus(serviceStatus?.apiStatus || "up")

    // if scheduledMaintenance exists, and scheduledMaintenance.endsAt is in the future, display as an upcoming schedule maintenance
    if (!!serviceStatus?.scheduledMaintenance) {
      const startDate = dateStringToDate(serviceStatus.scheduledMaintenance.startsAt, true)
      const endDate = dateStringToDate(serviceStatus.scheduledMaintenance.endsAt, true)

      if (isPast(endDate)) return

      const id = stringToHash(
        `${serviceStatus.scheduledMaintenance.startsAt}-${serviceStatus.scheduledMaintenance.endsAt}`,
      )

      addNotifications([
        {
          id,
          title: `Upcoming maintenance`,
          text: `SANS Foundations will be down for scheduled maintenance on ${format(startDate, `MMMM do`)}, between ${format(startDate, `HH:mm`)} and ${format(endDate, `HH:mm`)}. The site will be unavailable during this time.`,
          variant: `warning`,
          links: [],
          onClose: () => dismissNotification(id),
        },
      ])
    }

    // if title and notice exists, display notice notification
    if (serviceStatus?.title && serviceStatus?.notice) {
      const id = stringToHash(`${serviceStatus?.title}-${serviceStatus?.notice}`)

      addNotifications([
        {
          id,
          title: serviceStatus?.title,
          text: serviceStatus?.notice,
          links: [],
          variant: "warning",
          onClose: () => dismissNotification(id),
        },
      ])
    }
  }

  useEffect(() => {
    setup()
  }, [])

  if (status === "down") return <ServiceStatusView />
  return <>{children}</>
}
